/**
 * AddRecipeModal
 *
 * Smart bottom sheet for adding recipes from URLs.
 *
 * Clipboard policy (see issue #532): the sheet NEVER reads the clipboard on its
 * own on the web — every browser gates `navigator.clipboard.readText()` behind a
 * permission prompt, so an automatic read makes the browser's own "Paste" dialog
 * appear instead of our sheet. Instead we *offer* the clipboard link:
 *   - where a read is silent (native, or a browser the cook has already
 *     allowed): peek on open and show the copied link as a suggestion
 *   - everywhere else: a "Paste" pill inside the field, tapped deliberately
 * The URL is never acted on without the user asking, and the link stays visible
 * and changeable in the preview state so you can never get stuck on a stale one.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  TouchableWithoutFeedback,
  TextInput,
  ActivityIndicator,
  Platform,
  Keyboard,
  useWindowDimensions,
} from 'react-native';
import * as Clipboard from 'expo-clipboard';
import * as ImagePicker from 'expo-image-picker';
import { useRouter } from 'expo-router';
import Feather from '@expo/vector-icons/Feather';
import { useTranslation } from 'react-i18next';
import { cookFacingError } from '../../i18n/cookFacingError';
import { Paprika, PaprikaLayout, PaprikaRadius, PaprikaShadows } from '../../styles/theme';
import { doc, setDoc } from 'firebase/firestore';
import { firestore, isFirebaseEnabled } from '../../services/firebaseConfig';
import { resolveDataScope, buildScopedPath } from '../../services/householdPaths';
import { getActiveHouseholdId } from '../../services/householdContext';
import unifiedRecipeService, { type SharedRecipe } from '../../services/unifiedRecipeService';
import { handOffDraftRecipe } from '../../services/draftRecipeHandoff';
import { trackEvent, captureError, trackLLMGeneration } from '../../services/analyticsService';
import { classifyOnImport } from '../../services/importClassification';
import { getAuthHeaders } from '../../services/apiClient';
import { attachStepIngredientMap } from '../../services/stepIngredientMapClient';
import { detectVideoPlatform, isUnstableSourceUrl, VideoPlatform } from '../../utils/recipeSource';
import {
  canPeekClipboardSilently,
  isImportableUrl,
  shouldOfferClipboardLink,
} from '../../utils/clipboardSuggestion';
import { classifyFetchFailure, resolveImportErrorCode, resolveScrapeErrorMessage } from '../../utils/fetchErrors';
import { isDevWeb, DEV_API_ORIGIN } from '../../config/apiBase';
import { useSelector } from 'react-redux';
import { setPendingImport } from '../../utils/pendingImport';
import { resolveReadyInMinutes } from '../../utils/parseRecipeDuration';
import { recipeLanguageField } from '../../utils/languageDetection';
import { requestTranslationAtWrite } from '../../services/recipeTranslationRequest';
import { RemoteImage } from '../shared/RemoteImage';
import { authHrefReturningTo } from '../../utils/postAuthReturn';
import { selectHasAccount } from '../../redux/authSlice';

/**
 * Client-side request ceilings, deliberately layered: the server gives up at
 * 15s, we give up at 18s, and the browser's own ~20s ceiling is never reached.
 * Whoever gives up first owns the message, so keeping that order means the user
 * always sees our wording rather than the browser's "Load failed".
 */
const SCRAPE_TIMEOUT_MS = 18_000;

/** Video imports transcribe and summarise — the UI already warns 30-60s. */
const VIDEO_IMPORT_TIMEOUT_MS = 120_000;

interface AddRecipeModalProps {
  visible: boolean;
  onClose: () => void;
  /** `draft`: the document is a blank the editor is about to fill — see recipe-detail. */
  onSuccess: (recipeId: string, options?: { openEdit?: boolean; draft?: boolean }) => void;
  userId: string;
  /**
   * A link carried over from a guest's blocked import, replayed after sign-up.
   * Prefilled rather than auto-fetched: landing straight in a spinner right
   * after creating an account hides what is happening and why.
   */
  initialUrl?: string | null;
}

type ModalState = 'empty' | 'fetching' | 'preview' | 'importing' | 'error' | 'auth-required';

type UrlType = 'website' | 'video';

/** How the recipe reached us: from the link itself, or as text the cook pasted. */
type ImportMethod = 'link' | 'text';

/** Fewer characters than this cannot hold ingredients and steps; mirrors the API. */
const MIN_PASTED_TEXT = 40;

interface ScrapedRecipeInstructionSection {
  name?: string;
  steps: string[];
}

interface ScrapedRecipe {
  title: string;
  description?: string;
  image?: string;
  prepTime?: string;
  cookTime?: string;
  totalTime?: string;
  recipeYield?: string;
  ingredients: string[];
  instructions: string[];
  instructionSections?: ScrapedRecipeInstructionSection[];
  sourceName?: string;
  sourceUrl: string;
  /** Set only when sourceUrl was replaced by the address the scrape resolved to (#587). */
  originalSourceUrl?: string;
  /**
   * The publisher's own schema.org category. The scraper has always extracted
   * this (api/_lib/recipeExtractor.ts) and the import used to throw it away,
   * which is most of why app-imported recipes arrived unclassified. Typed
   * loosely because schema.org permits a string or an array and real sites
   * send both.
   */
  recipeCategory?: string | string[];
  diets?: string[];
  // Video-specific fields
  videoUrl?: string;
  videoPlatform?: VideoPlatform;
  videoThumbnail?: string;
}

const getApiBaseUrl = (): string => {
  if (isDevWeb()) return DEV_API_ORIGIN;
  // Not same-origin: this modal is also reachable outside the deployed web app.
  return 'https://duchef.app';
};

/**
 * Get display name for video platform
 */
const getVideoPlatformDisplayName = (platform: VideoPlatform): string => {
  const names: Record<VideoPlatform, string> = {
    instagram: 'Instagram',
    facebook: 'Facebook',
    youtube: 'YouTube',
    tiktok: 'TikTok',
  };
  return names[platform];
};

/**
 * Extract readable source name from URL
 */
const getSourceDisplayName = (url: string): string => {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    const knownSources: Record<string, string> = {
      'cooking.nytimes.com': 'NYT Cooking',
      'www.nytimes.com': 'New York Times',
      'www.bonappetit.com': 'Bon Appetit',
      'www.seriouseats.com': 'Serious Eats',
      'www.epicurious.com': 'Epicurious',
      'www.foodnetwork.com': 'Food Network',
      'www.allrecipes.com': 'AllRecipes',
      'www.delish.com': 'Delish',
      'www.food52.com': 'Food52',
      'www.thekitchn.com': 'The Kitchn',
      'www.simplyrecipes.com': 'Simply Recipes',
      'www.budgetbytes.com': 'Budget Bytes',
    };

    if (knownSources[hostname]) {
      return knownSources[hostname];
    }

    // Fallback: clean up hostname
    return hostname.replace(/^www\./, '').split('.')[0];
  } catch {
    return 'Recipe';
  }
};

/** The site a link points at, as the cook would name it: "ricardocuisine.com". */
const getHostname = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

/**
 * Validate URL format
 */
const isValidUrl = (text: string): boolean => isImportableUrl(text);

export const AddRecipeModal: React.FC<AddRecipeModalProps> = ({
  visible,
  onClose,
  onSuccess,
  userId,
  initialUrl,
}) => {
  const [modalState, setModalState] = useState<ModalState>('empty');
  // What the sign-up wall says: a pasted link to import, or a recipe photo to
  // read. Both need an account; only the sentence differs.
  const [authWallKind, setAuthWallKind] = useState<'import' | 'scan'>('import');
  const [url, setUrl] = useState('');
  const [preview, setPreview] = useState<ScrapedRecipe | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isVideo, setIsVideo] = useState(false);
  const [videoPlatform, setVideoPlatform] = useState<VideoPlatform | null>(null);
  // A link we found in the clipboard and are *offering* — never imported on its own
  const [clipboardSuggestion, setClipboardSuggestion] = useState<string | null>(null);
  // The way out when a post cannot be read from here: the cook copies the
  // recipe out of the app they are signed in to and pastes it. Kept across a
  // failed attempt so a second try does not start from a blank box.
  const [pastedText, setPastedText] = useState('');
  const [importMethod, setImportMethod] = useState<ImportMethod>('link');

  const { t } = useTranslation();

  // Importing writes a recipe to the signed-in user's collection and spends a
  // metered AI call, so it needs an account. Guests previously reached the
  // network anyway and were bounced by the API with a raw English 401.
  // Both flags matter: `isGuest` starts true and only `continueAsGuest` clears
  // it, while `isAuthenticated` is what a real sign-in sets. The server enforces
  // the same rule independently — this gate exists so the user gets a sentence
  // they can act on instead of a failed request.
  const isAuthenticated = useSelector(selectHasAccount);

  // Router for navigation
  const router = useRouter();

  // Ref for auto-focusing input
  const inputRef = useRef<TextInput>(null);

  // Last link we successfully imported. The clipboard still holds it afterwards,
  // so without this the sheet would keep offering the recipe you just added
  // and there'd be no way to add a second one (issue #532).
  const lastImportedUrlRef = useRef<string | null>(null);

  // The link a fetch is running for, so the same link is never fetched twice at once.
  const fetchingUrlRef = useRef<string | null>(null);

  // Still open? A clipboard answer can arrive after the sheet has closed.
  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  // Layout follows the window's shape, not the device (DESIGN.md): a wide
  // window gets a centred dialog, a narrow one the bottom sheet.
  const { width: windowWidth } = useWindowDimensions();
  const isDialog = windowWidth >= PaprikaLayout.tabletMinWidth;

  // "Take a photo" only where there is a camera to take it with. With a mouse
  // the browser opens the same file picker as "From a photo".
  const hasCamera = useMemo(
    () => Platform.OS !== 'web'
      || (typeof window !== 'undefined' && Boolean(window.matchMedia?.('(pointer: coarse)').matches)),
    [],
  );

  // Reset state when the sheet opens. Nothing is fetched here on purpose —
  // opening "+" must always land on the add-a-recipe sheet, never on a preview
  // and never on the browser's paste prompt.
  useEffect(() => {
    if (visible) {
      setModalState('empty');
      setUrl(initialUrl ?? '');
      setPreview(null);
      setError(null);
      setIsVideo(false);
      setVideoPlatform(null);
      setClipboardSuggestion(null);
      setPastedText('');
      setImportMethod('link');
      // A resumed link is already the one the user wants, so don't also offer
      // the clipboard — that would replace it with whatever they copied since.
      if (!initialUrl) {
        peekClipboard();
      }
    }
  }, [visible, initialUrl]);

  // Look at the clipboard and offer the link as a suggestion — only where the
  // platform lets us read it without putting its own prompt over the sheet.
  const peekClipboard = useCallback(async () => {
    try {
      if (!(await canPeekClipboardSilently(Platform.OS))) return;
      const clipboardContent = await Clipboard.getStringAsync();
      if (!visibleRef.current) return;
      if (shouldOfferClipboardLink(clipboardContent, lastImportedUrlRef.current)) {
        setClipboardSuggestion(clipboardContent.trim());
      }
    } catch (err) {
      console.log('Could not read clipboard:', err);
    }
  }, []);

  /**
   * Send an import request and land in preview or error. Shared by the link
   * path and the pasted-text path so both fail the same way and report the
   * same analytics; only the body and the endpoint differ.
   */
  const runImport = useCallback(async (
    body: { url?: string; text?: string },
    { recipeUrl, videoUrl, detectedPlatform, method }: {
      recipeUrl: string;
      videoUrl: boolean;
      detectedPlatform: VideoPlatform | null;
      method: ImportMethod;
    },
  ) => {
    // Stop before the network. A guest's request could only ever come back 401,
    // and the modal then showed the API's own English wording ("Missing auth
    // token") to someone who had done nothing wrong. Six retries in 65 seconds
    // is what that looked like in production. Here, on the one path every
    // import takes, so a pasted recipe is gated exactly like a link.
    if (!isAuthenticated) {
      setError(null);
      setAuthWallKind('import');
      setModalState('auth-required');
      // Held across the sign-up round trip so the link survives the detour.
      void setPendingImport(recipeUrl);
      trackEvent('import_blocked_guest', {
        url: recipeUrl,
        urlType: videoUrl ? 'video' : 'website',
        platform: detectedPlatform ?? 'website',
      });
      return;
    }

    setIsVideo(videoUrl);
    setVideoPlatform(detectedPlatform);
    setImportMethod(method);

    setModalState('fetching');
    setError(null);

    // Bound the request ourselves. Without this the browser owns the failure:
    // Safari drops a request that outlives its ceiling and reports it as
    // `TypeError: Load failed`, which we would then show verbatim to a cook.
    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, videoUrl ? VIDEO_IMPORT_TIMEOUT_MS : SCRAPE_TIMEOUT_MS);

    try {
      // Route to appropriate API endpoint
      const endpoint = videoUrl ? '/api/recipes/media-import' : '/api/recipes/scrape';
      const apiUrl = `${getApiBaseUrl()}${endpoint}`;

      console.log(`🔗 Importing from ${method === 'text' ? 'pasted text' : videoUrl ? 'video' : 'website'} URL:`, recipeUrl);

      const fetchHeaders = await getAuthHeaders();
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: fetchHeaders,
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      // An error page from the platform (rather than our handler) isn't JSON;
      // don't let the parse failure masquerade as the recipe being unreadable.
      let data: any;
      try {
        data = await response.json();
      } catch {
        throw new Error(t('addRecipe.errorNetwork'));
      }

      if (!response.ok || !data.success) {
        // Carry the API's failure code so the catch can pick our own wording
        // for it rather than showing the English message the server wrote.
        const apiError: Error & { code?: string } = new Error(data.error || 'Failed to fetch recipe');
        apiError.code = data.code;
        throw apiError;
      }

      // Track video import LLM call client-side (server can't use posthog-js)
      if (videoUrl && data.llm_metrics) {
        trackLLMGeneration({
          $ai_trace_id: Math.random().toString(36).slice(2) + Date.now().toString(36),
          $ai_span_name: method === 'text' ? 'text_recipe_import' : 'video_recipe_import',
          $ai_model: data.llm_metrics.model,
          $ai_provider: 'google',
          $ai_input_tokens: data.llm_metrics.input_tokens,
          $ai_output_tokens: data.llm_metrics.output_tokens,
          $ai_latency: data.llm_metrics.latency_s,
        });
      }

      // Prefer where the scrape actually landed over what was pasted. A link
      // copied out of a newsletter is a click-tracking wrapper whose token
      // rotates and expires, so storing it means the recipe can never be
      // refreshed again — the whole of #575. Refuse a resolution that is itself
      // unstable (an expired token lands on an ad tracker), and keep the pasted
      // URL in originalSourceUrl so the trail survives. See #587.
      // A Facebook share link resolves the same way: the API answers with the
      // public video page it landed on, in `recipe.sourceUrl`.
      const scrapeResolvedUrl: string | undefined = data.resolvedUrl ?? (videoUrl ? data.recipe?.sourceUrl : undefined);
      const durableUrl =
        scrapeResolvedUrl && !isUnstableSourceUrl(scrapeResolvedUrl)
          ? scrapeResolvedUrl
          : recipeUrl;

      setPreview({
        ...data.recipe,
        sourceUrl: durableUrl,
        ...(durableUrl !== recipeUrl && { originalSourceUrl: recipeUrl }),
        // Include video-specific fields if present
        videoUrl: data.recipe.videoUrl,
        videoPlatform: data.metadata?.platform || detectedPlatform,
        videoThumbnail: data.metadata?.thumbnail || data.recipe.videoThumbnail,
      });
      setModalState('preview');
    } catch (err: any) {
      console.error('Error fetching recipe:', err);

      // Separate "we never got an answer" from "the server answered no". Only
      // the latter carries a message worth showing; the former used to leak the
      // browser's own wording ("Load failed") straight into the modal.
      const failureKind = classifyFetchFailure(err, timedOut);
      const errorMessage =
        failureKind === 'timeout'
          ? t('addRecipe.errorTimeout')
          : failureKind === 'network'
            ? t('addRecipe.errorNetwork')
            : resolveScrapeErrorMessage(err.code, t);

      setError(errorMessage);
      setModalState('error');

      // The property the dashboards break down on, and the one the exception is
      // grouped by. It has to survive every failure path — including the ones
      // where the server never answered and there is no code to read off a body.
      const importErrorCode = resolveImportErrorCode(failureKind, err?.code);

      trackEvent('recipe_import_failed', {
        url: recipeUrl,
        error: errorMessage,
        failureKind,
        errorCode: importErrorCode,
        rawError: err?.message,
        urlType: videoUrl ? 'video' : 'website',
        platform: detectedPlatform ?? 'website',
        importMethod: method,
      });

      // Group by failure class, not by throw site. Every failure here is thrown
      // from the same line, so PostHog fingerprinted a bot wall, a Facebook
      // login page, an unreadable recipe and an auth refusal into a single
      // issue — whose title described whichever arrived first. The Discord
      // alert then quoted a message that had nothing to do with the issue it
      // linked to, which is how a stale June title reached a September error.
      captureError(
        err instanceof Error ? err : new Error(errorMessage),
        {
          url: recipeUrl,
          urlType: videoUrl ? 'video' : 'website',
          failureKind,
          importMethod: method,
          $exception_fingerprint: `recipe-import:${importErrorCode}`,
        }
      );
    } finally {
      clearTimeout(timeoutId);
    }
  }, [isAuthenticated, t]);

  // Fetch recipe preview from URL
  const fetchPreview = useCallback(async (recipeUrl: string) => {
    if (!isValidUrl(recipeUrl)) {
      setError(t('addRecipe.invalidUrl'));
      setModalState('error');
      // An import the user asked for that we refused before touching the
      // network is still a failed import. Untracked, these were missing from
      // the failure rate entirely rather than showing up as a category.
      // No `failureKind` — that describes how a fetch died, and there was none.
      trackEvent('recipe_import_failed', {
        url: recipeUrl,
        error: t('addRecipe.invalidUrl'),
        errorCode: 'invalid-url',
        urlType: 'website',
        platform: 'website',
      });
      return;
    }

    // One request per link. Leaving the field fires its blur, which asks for
    // the link the paste already asked for — two scrapes for one gesture
    // whenever the field had focus (always, on a desktop).
    if (fetchingUrlRef.current === recipeUrl) return;
    fetchingUrlRef.current = recipeUrl;

    // Detect if this is a video URL
    const detectedPlatform = detectVideoPlatform(recipeUrl);
    try {
      await runImport({ url: recipeUrl }, {
        recipeUrl,
        videoUrl: detectedPlatform !== null,
        detectedPlatform,
        method: 'link',
      });
    } finally {
      fetchingUrlRef.current = null;
    }
  }, [isAuthenticated, runImport, t]);

  // Import the recipe text the cook pasted after the link itself could not be
  // read. Always a video-platform post here (the box only appears for those),
  // and the link stays attached so the recipe still points at its post.
  const importPastedText = useCallback(async () => {
    const text = pastedText.trim();
    if (text.length < MIN_PASTED_TEXT) return;
    const recipeUrl = url.trim();
    await runImport({ text, url: recipeUrl || undefined }, {
      recipeUrl,
      videoUrl: true,
      detectedPlatform: detectVideoPlatform(recipeUrl),
      method: 'text',
    });
  }, [pastedText, url, runImport]);

  // Handle paste button press. This is always user-initiated, so the browser's
  // own paste prompt (if any) appears right under the finger that asked for it.
  const handlePaste = useCallback(async () => {
    try {
      const clipboardContent = await Clipboard.getStringAsync();
      const trimmed = clipboardContent?.trim();
      if (trimmed) {
        setUrl(trimmed);
        setClipboardSuggestion(null);
        if (isValidUrl(trimmed)) {
          fetchPreview(trimmed);
        }
        return;
      }
      // Empty or unreadable clipboard — let the user paste by hand
      inputRef.current?.focus();
    } catch (err) {
      console.log('Could not read clipboard:', err);
      // Permission refused (or unsupported) — fall back to a manual paste
      inputRef.current?.focus();
    }
  }, [fetchPreview]);

  // Accept the offered clipboard link
  const handleUseSuggestion = useCallback(() => {
    if (!clipboardSuggestion) return;
    setUrl(clipboardSuggestion);
    setClipboardSuggestion(null);
    fetchPreview(clipboardSuggestion);
  }, [clipboardSuggestion, fetchPreview]);

  // Back out of a preview to pick a different link (issue #532: without this the
  // preview replaced the input and the only way out was to close the sheet).
  const handleChangeLink = useCallback(() => {
    setUrl('');
    setPreview(null);
    setError(null);
    setIsVideo(false);
    setVideoPlatform(null);
    setClipboardSuggestion(null);
    setModalState('empty');
    inputRef.current?.focus();
  }, []);

  // Handle native paste event (works reliably on iOS Safari)
  const handleNativePaste = useCallback((e: any) => {
    // Get pasted text from the clipboard event
    const pastedText = e.clipboardData?.getData('text') ||
                       e.nativeEvent?.clipboardData?.getData('text');

    if (pastedText && pastedText.trim()) {
      const trimmed = pastedText.trim();
      setUrl(trimmed);

      if (isValidUrl(trimmed)) {
        e.preventDefault?.();
        fetchPreview(trimmed);
      }
    }
  }, [fetchPreview]);

  // Handle URL input blur - auto-fetch if valid
  const handleUrlBlur = useCallback(() => {
    if (url && isValidUrl(url) && modalState === 'empty') {
      fetchPreview(url);
    }
  }, [url, modalState, fetchPreview]);

  // Handle Add Recipe button
  const handleAddRecipe = useCallback(async () => {
    if (!preview) return;

    if (!userId) {
      setError(t('addRecipe.loginRequired'));
      setModalState('error');
      return;
    }

    setModalState('importing');
    Keyboard.dismiss();

    try {
      if (!isFirebaseEnabled() || !firestore) {
        throw new Error('Firebase not enabled');
      }

      // Generate unique document ID
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 8);
      const documentId = `saved-${timestamp}-${random}`;

      const scope = resolveDataScope(userId, getActiveHouseholdId());
      if (!scope) throw new Error('Cannot resolve data scope');
      const [root, scopeId, ...rest] = buildScopedPath(scope, 'savedRecipes', documentId);
      const recipeRef = doc(firestore, root, scopeId, ...rest);

      // Build recipe data
      // Classify before the write so the recipe lands with a dish type instead
      // of arriving blank and waiting for someone to fix it in the quality
      // checker. Sync layers only — pure, no API call, no latency.
      const dishTypes = classifyOnImport({
        importPath: 'app-import',
        title: preview.title,
        rawCategory: preview.recipeCategory,
        sourceUrl: preview.sourceUrl,
      });

      // The raw strings below are kept for reference, but nothing reads them:
      // `convertToRecipe` maps only the numeric minute fields, so a recipe
      // imported in-app used to arrive with no time at all and show up as
      // "Temps manquant". Derive them here. Handles both the ISO-8601 a scrape
      // returns and the prose Gemini returns for a video or pasted text.
      const timing = resolveReadyInMinutes(preview);

      const recipeData: Record<string, any> = {
        id: documentId,
        title: preview.title,
        description: preview.description || '',
        image: preview.image || preview.videoThumbnail || '',
        prepTime: preview.prepTime || '',
        cookTime: preview.cookTime || '',
        totalTime: preview.totalTime || '',
        ...(timing.readyInMinutes > 0 && { readyInMinutes: timing.readyInMinutes }),
        ...(timing.preparationMinutes > 0 && { preparationMinutes: timing.preparationMinutes }),
        ...(timing.cookingMinutes > 0 && { cookingMinutes: timing.cookingMinutes }),
        recipeYield: preview.recipeYield || '4 servings',
        // Keep the publisher's category. Discarding it was throwing away the
        // single strongest classification signal we get for free.
        recipeCategory: Array.isArray(preview.recipeCategory)
          ? preview.recipeCategory.filter(c => typeof c === 'string').join(', ')
          : preview.recipeCategory || '',
        recipeCuisine: '',
        dishTypes,
        // Without it the planner and the grocery list never translate the recipe.
        ...recipeLanguageField([preview.title, ...(preview.ingredients || []), ...(preview.instructions || [])]),
        ingredients: preview.ingredients || [],
        instructions: (preview.instructions || []).map((step, idx) => ({ number: idx + 1, step })),
        ...(preview.instructionSections?.length
          ? {
              instructionSections: preview.instructionSections.map(section => ({
                name: section.name,
                steps: (section.steps || []).map((step, idx) => ({ number: idx + 1, step })),
              })),
            }
          : {}),
        author: preview.sourceName || getSourceDisplayName(preview.sourceUrl),
        keywords: '',
        nutrition: null,
        diets: preview.diets || [],
        sourceUrl: preview.sourceUrl,
        ...(preview.originalSourceUrl && { originalSourceUrl: preview.originalSourceUrl }),
        sourceName: preview.sourceName || getSourceDisplayName(preview.sourceUrl),
        savedAt: new Date().toISOString(),
        source: 'app-import' as const,
        type: 'imported' as const,
        userId,
      };

      // Add video-specific fields if this is a video recipe (only add defined values)
      if (preview.videoUrl) {
        recipeData.videoUrl = preview.videoUrl;
        if (preview.videoPlatform) {
          recipeData.videoPlatform = preview.videoPlatform;
        }
        if (preview.videoThumbnail) {
          recipeData.videoThumbnail = preview.videoThumbnail;
        }
      }

      await setDoc(recipeRef, recipeData);
      console.log('Recipe saved:', preview.title);

      // The step→ingredient map, asked for after the save and written back
      // when it lands (#857). Not awaited: the cook is not kept waiting on a
      // model call, and a map that never comes leaves the recipe on the
      // string matcher, which is what it had.
      void attachStepIngredientMap(
        recipeRef,
        { title: preview.title, ingredients: preview.ingredients || [], steps: preview.instructions || [] },
        'app-import',
      );
      requestTranslationAtWrite(documentId, 'app-import');

      // Track successful import in PostHog
      trackEvent('recipe_imported', {
        source: preview.videoUrl ? 'video' : 'website',
        importMethod,
        platform: preview.videoPlatform,
        sourceName: preview.sourceName,
        ingredientCount: preview.ingredients?.length || 0,
        instructionCount: preview.instructions?.length || 0,
      });

      // Invalidate cache so recipe list reloads with new recipe
      unifiedRecipeService.invalidateCache();

      // Remember it so the still-unchanged clipboard doesn't offer it again
      lastImportedUrlRef.current = preview.originalSourceUrl ?? preview.sourceUrl;

      onSuccess(documentId);
    } catch (err: any) {
      console.error('Error saving recipe:', err);
      setError(cookFacingError(err, t, 'addRecipe.saveFailed'));
      setModalState('error');
    }
  }, [preview, userId, onSuccess, t]);

  // Handle close with reset
  const handleClose = useCallback(() => {
    setModalState('empty');
    setUrl('');
    setPreview(null);
    setError(null);
    setIsVideo(false);
    setVideoPlatform(null);
    setClipboardSuggestion(null);
    onClose();
  }, [onClose]);

  const handlePhotoSelected = useCallback((uri: string, source: 'library' | 'camera') => {
    handleClose();
    router.push({
      pathname: '/(app)/photo-recipe-review',
      params: { imageUri: uri, source },
    });
  }, [handleClose, router]);

  // Reading a recipe photo is the same metered call as a scan (#775): a guest
  // is stopped before the picker, not after the photo is taken.
  const blockGuestScan = useCallback((): boolean => {
    if (isAuthenticated) return false;
    setError(null);
    setAuthWallKind('scan');
    setModalState('auth-required');
    trackEvent('scan_blocked_guest', { source: 'add_recipe', kind: 'recipe' });
    return true;
  }, [isAuthenticated]);

  const handlePickFromLibrary = useCallback(async () => {
    if (blockGuestScan()) return;
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 1,
    });
    if (!result.canceled && result.assets[0]?.uri) {
      handlePhotoSelected(result.assets[0].uri, 'library');
    }
  }, [handlePhotoSelected, blockGuestScan]);

  const handleTakePhoto = useCallback(async () => {
    if (blockGuestScan()) return;
    const result = await ImagePicker.launchCameraAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 1,
    });
    if (!result.canceled && result.assets[0]?.uri) {
      handlePhotoSelected(result.assets[0].uri, 'camera');
    }
  }, [handlePhotoSelected, blockGuestScan]);

  // Try again from error state
  const handleTryAgain = useCallback(() => {
    setError(null);
    if (url && isValidUrl(url)) {
      fetchPreview(url);
    } else {
      setModalState('empty');
    }
  }, [url, fetchPreview]);

  // Create a blank recipe manually and navigate to edit it
  const handleAddManually = useCallback(async () => {
    if (!userId) {
      setError(t('addRecipe.loginRequired'));
      setModalState('error');
      return;
    }

    setModalState('importing');
    Keyboard.dismiss();

    try {
      if (!isFirebaseEnabled() || !firestore) {
        throw new Error('Firebase not enabled');
      }

      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 8);
      const documentId = `saved-${timestamp}-${random}`;

      const scope = resolveDataScope(userId, getActiveHouseholdId());
      if (!scope) throw new Error('Cannot resolve data scope');
      const [root, scopeId, ...rest] = buildScopedPath(scope, 'savedRecipes', documentId);
      const recipeRef = doc(firestore, root, scopeId, ...rest);

      const draft = {
        id: documentId,
        title: t('addRecipe.newRecipeTitle'),
        description: '',
        image: '',
        prepTime: '',
        cookTime: '',
        totalTime: '',
        recipeYield: '4',
        recipeCategory: '',
        recipeCuisine: '',
        ingredients: [],
        instructions: [],
        author: '',
        keywords: '',
        nutrition: null,
        diets: [],
        sourceUrl: url || '',
        sourceName: '',
        savedAt: new Date().toISOString(),
        source: 'manual' as const,
        type: 'imported' as const,
        userId,
      };
      await setDoc(recipeRef, draft);

      trackEvent('recipe_created_manually', {
        hadUrl: !!url,
        fromError: modalState === 'error',
      });

      unifiedRecipeService.invalidateCache();
      // The detail screen opens this from memory: reading back what was just
      // written kept a Safari cook on the loading screen for 30 s (#791).
      handOffDraftRecipe(documentId, unifiedRecipeService.recipeFromDocument(draft as unknown as SharedRecipe, documentId));
      onSuccess(documentId, { openEdit: true, draft: true });
    } catch (err: any) {
      console.error('Error creating manual recipe:', err);
      setError(cookFacingError(err, t, 'addRecipe.createFailed'));
      setModalState('error');
    }
  }, [userId, url, modalState, onSuccess, t]);

  const importing = modalState === 'importing';
  const previewSource = preview
    ? preview.videoPlatform
      ? getVideoPlatformDisplayName(preview.videoPlatform)
      : preview.sourceName || getSourceDisplayName(preview.sourceUrl)
    : '';

  return (
    <Modal
      visible={visible}
      animationType={isDialog ? 'fade' : 'slide'}
      transparent={true}
      onRequestClose={handleClose}
    >
      <TouchableOpacity
        style={[styles.overlay, isDialog && styles.overlayDialog]}
        activeOpacity={1}
        onPress={handleClose}
      >
        <TouchableWithoutFeedback onPress={() => {}}>
          <View style={[styles.sheet, isDialog && styles.dialog]} testID="add-recipe-sheet">
          {!isDialog && <View style={styles.dragIndicator} />}

          {/* One title for every state: the content below says what is happening */}
          <View style={styles.header}>
            <Text style={styles.title}>{t('addRecipe.title')}</Text>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={handleClose}
              disabled={importing}
              accessibilityRole="button"
              accessibilityLabel={t('common.close')}
              testID="add-recipe-close"
            >
              <Feather name="x" size={18} color={Paprika.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* Content based on state */}
          {modalState === 'empty' && (
            <>
              {!!clipboardSuggestion && (
                /* A link we found — offered, not imported */
                <View style={styles.suggestionCard} testID="clipboard-suggestion">
                  <Feather name="link" size={18} color={Paprika.primary} />
                  <View style={styles.suggestionTextWrap}>
                    <Text style={styles.suggestionLabel}>{t('addRecipe.linkDetected')}</Text>
                    <Text style={styles.suggestionSource} numberOfLines={1}>
                      {getHostname(clipboardSuggestion)}
                    </Text>
                  </View>
                  <TouchableOpacity
                    style={styles.primaryPill}
                    onPress={handleUseSuggestion}
                    testID="use-clipboard-suggestion"
                  >
                    <Text style={styles.primaryPillText}>{t('addRecipe.addButton')}</Text>
                  </TouchableOpacity>
                </View>
              )}

              <View style={styles.field}>
                <TextInput
                  ref={inputRef}
                  style={styles.input}
                  placeholder={clipboardSuggestion
                    ? t('addRecipe.anotherLinkPlaceholder')
                    : t('addRecipe.pastePlaceholder')}
                  placeholderTextColor={Paprika.textMuted}
                  value={url}
                  onChangeText={(text) => {
                    setUrl(text);
                    // Auto-fetch when a valid URL is pasted
                    if (isValidUrl(text.trim())) {
                      fetchPreview(text.trim());
                    }
                  }}
                  onBlur={handleUrlBlur}
                  onSubmitEditing={() => { if (url.trim()) fetchPreview(url.trim()); }}
                  returnKeyType="go"
                  // A mouse-and-keyboard window: Cmd+V lands in the field at
                  // once. Never on a phone, where focus raises the keyboard
                  // over the other ways in.
                  autoFocus={isDialog && !hasCamera}
                  autoCapitalize="none"
                  autoCorrect={false}
                  keyboardType="url"
                  // Web-specific: handle paste event + disable AutoFill
                  {...(Platform.OS === 'web' ? {
                    onPaste: handleNativePaste,
                    autoComplete: 'off',
                    'data-lpignore': 'true',
                    'data-form-type': 'other',
                  } : {})}
                />
                {url ? (
                  /* Something to add: the only moment an Add button exists */
                  <TouchableOpacity
                    style={styles.primaryPill}
                    onPress={() => fetchPreview(url.trim())}
                    testID="add-link-button"
                  >
                    <Text style={styles.primaryPillText}>{t('addRecipe.addButton')}</Text>
                  </TouchableOpacity>
                ) : !clipboardSuggestion ? (
                  /* One deliberate tap to paste — the browser's own prompt, if
                     any, appears under the finger that asked for it */
                  <TouchableOpacity
                    style={styles.pastePill}
                    onPress={handlePaste}
                    accessibilityLabel={t('addRecipe.pasteLink')}
                    testID="paste-link-button"
                  >
                    <Feather name="clipboard" size={16} color={Paprika.primary} />
                    <Text style={styles.pastePillText}>{t('addRecipe.paste')}</Text>
                  </TouchableOpacity>
                ) : null}
              </View>

              <View style={styles.methods}>
                <TouchableOpacity style={styles.methodTile} onPress={handlePickFromLibrary} testID="from-photo-tile">
                  <Feather name="image" size={18} color={Paprika.primary} />
                  <Text style={styles.methodText}>{t('addRecipe.fromPhoto')}</Text>
                  {!isAuthenticated && <ScanLock label={t('scanGate.lockedHint')} />}
                </TouchableOpacity>
                {hasCamera && (
                  <TouchableOpacity style={styles.methodTile} onPress={handleTakePhoto} testID="take-photo-tile">
                    <Feather name="camera" size={18} color={Paprika.primary} />
                    <Text style={styles.methodText}>{t('addRecipe.takePhoto')}</Text>
                    {!isAuthenticated && <ScanLock label={t('scanGate.lockedHint')} />}
                  </TouchableOpacity>
                )}
                <TouchableOpacity
                  style={styles.methodTile}
                  onPress={handleAddManually}
                  accessibilityLabel={t('addRecipe.addManually')}
                  testID="add-manually-link"
                >
                  <Feather name="edit-3" size={18} color={Paprika.primary} />
                  <Text style={styles.methodText}>{t('addRecipe.writeIt')}</Text>
                </TouchableOpacity>
              </View>
            </>
          )}

          {modalState === 'fetching' && (
            <View style={styles.fetchingContainer}>
              <ActivityIndicator size="large" color={Paprika.primary} />
              <Text style={styles.fetchingText}>
                {isVideo ? t('addRecipe.analyzingVideo') : t('addRecipe.fetchingRecipe')}
              </Text>
              <Text style={styles.fetchingSource} numberOfLines={1}>
                {isVideo && videoPlatform
                  ? getVideoPlatformDisplayName(videoPlatform)
                  : getSourceDisplayName(url)}
              </Text>
              {isVideo && (
                <Text style={styles.fetchingHint}>
                  {t('addRecipe.videoHint')}
                </Text>
              )}
            </View>
          )}

          {(modalState === 'preview' || importing) && preview && (
            <View style={styles.previewContainer}>
              {/* The link is named once, as the recipe's source */}
              <View style={[styles.previewCard, importing && styles.previewCardImporting]}>
                <View style={styles.previewImageWrapper}>
                  <RemoteImage
                    uri={preview.image || preview.videoThumbnail}
                    style={styles.previewImage}
                    resizeMode="cover"
                    fallback={
                      <View style={styles.previewImagePlaceholder}>
                        <Feather name={preview.videoUrl ? 'video' : 'image'} size={24} color={Paprika.textMuted} />
                      </View>
                    }
                  />
                  {!!preview.videoUrl && (
                    <View style={styles.videoPlayOverlay}>
                      <Feather name="play-circle" size={24} color={Paprika.onPrimary} />
                    </View>
                  )}
                </View>
                <View style={styles.previewInfo}>
                  <Text style={styles.previewTitle} numberOfLines={2}>
                    {preview.title}
                  </Text>
                  <Text style={styles.previewMeta} numberOfLines={1}>
                    {preview.ingredients.length > 0
                      ? `${previewSource} · ${t('addRecipe.ingredientsCount', { count: preview.ingredients.length })}`
                      : previewSource}
                  </Text>
                </View>
              </View>

              {importing ? (
                <View style={styles.importingIndicator}>
                  <ActivityIndicator size="small" color={Paprika.primary} />
                  <Text style={styles.importingText}>{t('addRecipe.savingRecipe')}</Text>
                </View>
              ) : (
                <View style={styles.previewActions}>
                  {/* Never a dead end: back to the field for a different link */}
                  <TouchableOpacity style={styles.textButton} onPress={handleChangeLink} testID="change-link-button">
                    <Text style={styles.textButtonText}>{t('addRecipe.changeLink')}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.primaryPill, styles.primaryPillLarge]}
                    onPress={handleAddRecipe}
                    testID="add-recipe-confirm"
                  >
                    <Text style={styles.primaryPillText}>{t('addRecipe.addRecipeButton')}</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          )}

          {/* "Write it" with no preview: the blank is being created */}
          {importing && !preview && (
            <View style={styles.fetchingContainer}>
              <ActivityIndicator size="large" color={Paprika.primary} />
            </View>
          )}

          {modalState === 'auth-required' && (
            <View style={styles.authWallContainer}>
              <View style={styles.authWallIcon}>
                <Feather name={authWallKind === 'scan' ? 'camera' : 'bookmark'} size={28} color={Paprika.primary} />
              </View>
              <Text style={styles.authWallTitle}>
                {t(authWallKind === 'scan' ? 'addRecipe.signInWall.scanTitle' : 'addRecipe.signInWall.title')}
              </Text>
              <Text style={styles.authWallBody}>
                {t(authWallKind === 'scan' ? 'addRecipe.signInWall.scanBody' : 'addRecipe.signInWall.body')}
              </Text>

              <TouchableOpacity
                style={styles.authWallPrimary}
                onPress={() => {
                  handleClose();
                  // A recipe photo comes back to Recipes, where the sheet lives.
                  router.push((authWallKind === 'scan' ? authHrefReturningTo('/recipes') : '/auth?mode=signup') as any);
                }}
              >
                <Text style={styles.authWallPrimaryText}>{t('addRecipe.signInWall.cta')}</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.authWallSecondary}
                onPress={() => {
                  handleClose();
                  router.push((authWallKind === 'scan' ? authHrefReturningTo('/recipes') : '/auth') as any);
                }}
              >
                <Text style={styles.authWallSecondaryText}>{t('addRecipe.signInWall.signIn')}</Text>
              </TouchableOpacity>
            </View>
          )}

          {modalState === 'error' && (
            <View style={styles.errorContainer}>
              <Feather name="alert-circle" size={28} color={Paprika.primary} />
              <Text style={styles.errorText}>{error}</Text>

              {isVideo && (
                /* The post could not be read from here — behind a login, or a
                   silent video whose recipe is in a comment. The cook is signed
                   in to that app already; the text is one copy away. */
                <View style={styles.pasteTextCard} testID="paste-text-fallback">
                  <Text style={styles.pasteTextTitle}>{t('addRecipe.pasteText.title')}</Text>
                  <Text style={styles.pasteTextBody}>
                    {t('addRecipe.pasteText.body', {
                      platform: videoPlatform ? getVideoPlatformDisplayName(videoPlatform) : t('addRecipe.pasteText.theApp'),
                    })}
                  </Text>
                  <TextInput
                    style={styles.pasteTextInput}
                    placeholder={t('addRecipe.pasteText.placeholder')}
                    placeholderTextColor={Paprika.textSecondary}
                    value={pastedText}
                    onChangeText={setPastedText}
                    multiline
                    textAlignVertical="top"
                    autoCapitalize="sentences"
                    testID="paste-text-input"
                  />
                  <TouchableOpacity
                    style={[
                      styles.pasteTextButton,
                      pastedText.trim().length < MIN_PASTED_TEXT && styles.pasteTextButtonDisabled,
                    ]}
                    onPress={importPastedText}
                    disabled={pastedText.trim().length < MIN_PASTED_TEXT}
                    testID="paste-text-submit"
                  >
                    <Text style={styles.pasteTextButtonText}>{t('addRecipe.pasteText.cta')}</Text>
                  </TouchableOpacity>
                </View>
              )}

              {/* The sign-in prompt lives in the `auth-required` state now. This
                  branch used to sniff for the English substring "log in" inside
                  an already-translated message, so it never fired in French. */}
              <View style={styles.errorActions}>
                <TouchableOpacity style={styles.textButton} onPress={handleAddManually} testID="error-add-manually">
                  <Text style={styles.textButtonText}>{t('addRecipe.addManually')}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.tryAgainButton} onPress={handleTryAgain} testID="error-try-again">
                  <Text style={styles.tryAgainText}>{t('addRecipe.tryAgain')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
          </View>
        </TouchableWithoutFeedback>
      </TouchableOpacity>
    </Modal>
  );
};

/** The corner lock on a tile a guest cannot use yet: reading a photo needs an account. */
const ScanLock: React.FC<{ label: string }> = ({ label }) => (
  <View style={styles.methodLock} accessibilityLabel={label} testID="scan-locked-hint">
    <Feather name="lock" size={11} color={Paprika.textMuted} />
  </View>
);

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(27, 28, 26, 0.5)',
    justifyContent: 'flex-end',
  },
  overlayDialog: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  sheet: {
    backgroundColor: Paprika.surfaceLowest,
    borderTopLeftRadius: PaprikaRadius.xl,
    borderTopRightRadius: PaprikaRadius.xl,
    paddingHorizontal: 16,
    paddingBottom: Platform.OS === 'ios' ? 34 : 24,
  },
  // A wide window gets a dialog, not a sheet stretched across it.
  dialog: {
    width: '100%',
    maxWidth: 460,
    borderRadius: PaprikaRadius.xl,
    padding: 24,
    paddingBottom: 24,
    ...PaprikaShadows.editorial,
  },
  dragIndicator: {
    width: 36,
    height: 4,
    backgroundColor: Paprika.surfaceHighest,
    borderRadius: 2,
    alignSelf: 'center',
    marginTop: 10,
    marginBottom: 14,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  title: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 24,
    lineHeight: 32,
    color: Paprika.textPrimary,
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: Paprika.surfaceLow,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // The field: an inset surface, no border (DESIGN.md, Inputs & Search). Its
  // one action sits inside it — Paste while empty, Add once there is a link.
  field: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    minHeight: 52,
    paddingLeft: 14,
    paddingRight: 6,
    borderRadius: PaprikaRadius.md,
    backgroundColor: Paprika.surfaceHighest,
  },
  input: {
    flex: 1,
    minWidth: 0,
    paddingVertical: 14,
    fontFamily: 'Inter-Regular',
    fontSize: 16,
    color: Paprika.textPrimary,
    ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as object) : null),
  },
  pastePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 40,
    paddingHorizontal: 16,
    borderRadius: PaprikaRadius.xl,
    backgroundColor: Paprika.surfaceLowest,
  },
  pastePillText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    color: Paprika.primary,
  },
  primaryPill: {
    height: 40,
    paddingHorizontal: 18,
    borderRadius: PaprikaRadius.xl,
    backgroundColor: Paprika.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryPillLarge: {
    flex: 1,
    height: 48,
  },
  primaryPillText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 15,
    color: Paprika.onPrimary,
  },
  textButton: {
    paddingVertical: 12,
    paddingHorizontal: 8,
  },
  textButtonText: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    color: Paprika.primary,
  },
  suggestionCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 10,
    paddingVertical: 10,
    paddingLeft: 14,
    paddingRight: 10,
    borderRadius: PaprikaRadius.md,
    backgroundColor: Paprika.surfaceLow,
  },
  suggestionTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  suggestionLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textMuted,
  },
  suggestionSource: {
    fontFamily: 'Inter-Medium',
    fontSize: 15,
    color: Paprika.textPrimary,
    marginTop: 2,
  },
  // The other ways in, as equals — none of them a footnote under the field.
  methods: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 20,
  },
  methodTile: {
    position: 'relative',
    flex: 1,
    alignItems: 'center',
    gap: 6,
    paddingVertical: 12,
    paddingHorizontal: 6,
    borderRadius: PaprikaRadius.md,
    backgroundColor: Paprika.surfaceLow,
  },
  methodLock: {
    position: 'absolute',
    top: 8,
    right: 8,
  },
  methodText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.textSecondary,
    textAlign: 'center',
  },
  fetchingContainer: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  fetchingText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 16,
    color: Paprika.textPrimary,
    marginTop: 16,
  },
  fetchingSource: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    color: Paprika.textSecondary,
    marginTop: 4,
  },
  fetchingHint: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textMuted,
    marginTop: 8,
  },
  previewContainer: {
    gap: 16,
  },
  previewCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 10,
    backgroundColor: Paprika.surfaceLow,
    borderRadius: PaprikaRadius.lg,
  },
  previewCardImporting: {
    opacity: 0.6,
  },
  previewImageWrapper: {
    width: 76,
    height: 76,
    borderRadius: PaprikaRadius.md,
    overflow: 'hidden',
  },
  previewImage: {
    width: 76,
    height: 76,
  },
  videoPlayOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(27, 28, 26, 0.3)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  previewImagePlaceholder: {
    width: 76,
    height: 76,
    backgroundColor: Paprika.surfaceHighest,
    justifyContent: 'center',
    alignItems: 'center',
  },
  previewInfo: {
    flex: 1,
    minWidth: 0,
  },
  previewTitle: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 19,
    lineHeight: 24,
    color: Paprika.textPrimary,
  },
  previewMeta: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textSecondary,
    marginTop: 4,
  },
  previewActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  importingIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 48,
    gap: 8,
  },
  importingText: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    color: Paprika.textSecondary,
  },
  errorContainer: {
    alignItems: 'center',
    paddingTop: 8,
  },
  errorText: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    color: Paprika.textSecondary,
    textAlign: 'center',
    marginTop: 12,
    marginBottom: 16,
    lineHeight: 20,
  },
  // Paste-the-recipe fallback. A tonal card on the sheet, the box a lighter
  // paper on top of it — depth from surfaces, not rules (DESIGN.md).
  pasteTextCard: {
    alignSelf: 'stretch',
    backgroundColor: Paprika.surfaceLow,
    borderRadius: PaprikaRadius.md,
    padding: 16,
    marginBottom: 16,
    gap: 10,
  },
  pasteTextTitle: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    fontWeight: '600',
    color: Paprika.textPrimary,
  },
  pasteTextBody: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    lineHeight: 18,
    color: Paprika.textSecondary,
  },
  pasteTextInput: {
    fontFamily: 'Inter-Regular',
    minHeight: 120,
    maxHeight: 220,
    paddingVertical: 12,
    paddingHorizontal: 14,
    fontSize: 14,
    lineHeight: 20,
    color: Paprika.textPrimary,
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: PaprikaRadius.md,
  },
  pasteTextButton: {
    backgroundColor: Paprika.primary,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 24,
    alignItems: 'center',
  },
  pasteTextButtonDisabled: {
    opacity: 0.4,
  },
  pasteTextButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    fontWeight: '600',
    color: Paprika.onPrimary,
  },
  // Sign-up wall. Tonal surfaces and generous spacing rather than boxes and
  // rules, per the paper-stacking rule in DESIGN.md.
  authWallContainer: {
    alignItems: 'center',
    paddingTop: 8,
    paddingHorizontal: 8,
  },
  authWallIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: Paprika.accentLight,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  authWallTitle: {
    // A "hero moment" — Newsreader carries these, Inter carries the chrome.
    fontFamily: 'Newsreader-Italic',
    fontSize: 24,
    fontStyle: 'italic',
    color: Paprika.textPrimary,
    textAlign: 'center',
    marginBottom: 8,
  },
  authWallBody: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    lineHeight: 20,
    color: Paprika.textSecondary,
    textAlign: 'center',
    marginBottom: 24,
  },
  authWallPrimary: {
    backgroundColor: Paprika.primary,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 24, // full pill, per the primary-button spec
    alignSelf: 'stretch',
    alignItems: 'center',
    marginBottom: 8,
  },
  authWallPrimaryText: {
    fontFamily: 'Inter-SemiBold',
    color: Paprika.onPrimary,
    fontSize: 15,
    fontWeight: '600',
  },
  authWallSecondary: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    alignSelf: 'stretch',
    alignItems: 'center',
  },
  authWallSecondaryText: {
    fontFamily: 'Inter-Medium',
    color: Paprika.primary,
    fontSize: 14,
    fontWeight: '500',
  },
  tryAgainButton: {
    flex: 1,
    height: 48,
    borderRadius: PaprikaRadius.xl,
    backgroundColor: Paprika.accentLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tryAgainText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 15,
    color: Paprika.primary,
  },
  errorActions: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
});

export default AddRecipeModal;
